import {
    StepState,
    stepStartState,
    stepCloneState,
    stepDevcontainerState,
    stepAutomationsState,
} from "@/components/environments/environment-start-steps-state";
import { WELCOME_MESSAGES_SHOWN_PREFERENCE_KEY } from "@/hooks/use-onboarding-completion";
import { usePreferenceValue } from "@/hooks/use-preference-value";
import type { Message } from "@/hooks/use-stream-conversation";
import type { PlainEnvironment } from "@/queries/environment-queries";
import { useOrganization } from "@/queries/organization-queries";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type UseMessagesWithWelcomeOptions = {
    messages: Message[];
    startCount: number | undefined;
    environmentId: string | undefined;
    hasAgentReply: boolean;
    environment: PlainEnvironment | undefined;
};

/**
 * Injects welcome messages into the conversation stream.
 *
 * On the first 1-3 environment starts, inserts lifecycle-progress fragments
 * (clone, devcontainer, etc.) that track real-time environment state. These are
 * persisted to localStorage once the agent replies so they survive page refresh.
 *
 * On subsequent starts (count 2-3), inserts welcome-back tips (projects,
 * automations, editor options) at the position where the restart occurred.
 */
export function useMessagesWithWelcome({ messages, startCount, environmentId, hasAgentReply, environment }: UseMessagesWithWelcomeOptions): Message[] {

    const { data: organization } = useOrganization();
    const { value: welcomeMessagesShown, deleteValue: deleteWelcomeShown } = usePreferenceValue(WELCOME_MESSAGES_SHOWN_PREFERENCE_KEY);
    const markWelcomeShown = useCallback(async () => {
        await deleteWelcomeShown();
    }, [deleteWelcomeShown]);
    const isWelcomeEligible = useMemo(() => {
        if (!organization) return false;
        if (welcomeMessagesShown !== "false") return false;
        const tier = organization.tier;
        return tier === OrganizationTier.FREE_ONA || tier === OrganizationTier.CORE;
    }, [organization, welcomeMessagesShown]);

    const { firstRunMessages, firstRunAnchorId } = useWelcomeMessages({ environment, startCount, hasAgentReply, environmentId, messages, isWelcomeEligible, markWelcomeShown });
    const welcomeBackAnchors = useWelcomeBackAnchors(messages, startCount, environmentId, isWelcomeEligible);
    const welcomeBackRevealed = useWelcomeBackReveal(welcomeBackAnchors);

    return useMemo(() => {
        if (firstRunMessages.length === 0 && welcomeBackAnchors.length === 0) {
            return messages;
        }

        let result: Message[];

        if (firstRunMessages.length === 0) {
            result = [...messages];
        } else if (firstRunAnchorId !== null) {
            // Persisted first-run messages — insert at their anchored position
            const anchorIndex = messages.findIndex((m) => m.id === firstRunAnchorId);
            if (anchorIndex !== -1) {
                result = [
                    ...messages.slice(0, anchorIndex + 1),
                    ...firstRunMessages,
                    ...messages.slice(anchorIndex + 1),
                ];
            } else {
                // Anchor not found (message may have been pruned) — prepend
                result = [...firstRunMessages, ...messages];
            }
        } else if (hasAgentReply) {
            // Active first-run with agent reply — insert before the first real agent message
            const completedFirstRun: Message[] = firstRunMessages.map((msg) => {
                if (msg.type !== "agent") {
                    return msg;
                }
                return {
                    ...msg,
                    fragments: msg.fragments.map((f) => ({ ...f, completed: true })),
                };
            });
            const firstAgentIndex = messages.findIndex((m) => m.type === "agent");
            result = [
                ...messages.slice(0, firstAgentIndex),
                ...completedFirstRun,
                ...messages.slice(firstAgentIndex),
            ];
        } else {
            // No agent reply yet — first-run messages go after user messages
            result = [...messages, ...firstRunMessages];
        }

        // Insert welcome-back messages at their anchored positions (reverse to preserve splice indices).
        if (welcomeBackAnchors.length > 0) {
            for (let anchorIdx = welcomeBackAnchors.length - 1; anchorIdx >= 0; anchorIdx--) {
                const anchor = welcomeBackAnchors[anchorIdx];
                if (!WELCOME_MESSAGES[anchor.count]) continue;
                const texts = WELCOME_MESSAGES[anchor.count];
                const revealed = welcomeBackRevealed.get(anchor.count) ?? texts.length;
                const visibleTexts = texts.slice(0, revealed);
                const allRevealed = revealed >= texts.length;
                const welcomeMsg: Message = {
                    id: -(anchor.count),
                    type: "agent" as const,
                    fragments: visibleTexts.map((text, i) => ({
                        type: "text" as const,
                        id: `welcome-msg-${anchor.count}-${i}`,
                        content: text,
                        completed: allRevealed || i < visibleTexts.length - 1,
                    })),
                };

                if (anchor.afterMessageId === null) {
                    result.unshift(welcomeMsg);
                    continue;
                }

                const anchorIndex = result.findIndex((m) => m.id === anchor.afterMessageId);
                if (anchorIndex !== -1) {
                    result.splice(anchorIndex + 1, 0, welcomeMsg);
                }
            }
        }

        return result;
    }, [messages, firstRunMessages, firstRunAnchorId, welcomeBackAnchors, welcomeBackRevealed, hasAgentReply]);
}

const WELCOME_BACK_ANCHORS_KEY = "WELCOME_BACK_ANCHORS";

/**
 * Lifecycle step definition. Each step maps to messages that accumulate
 * as the step progresses. `runningTexts` appear while the step is in progress,
 * and `doneText` is appended when the step completes — nothing is removed.
 */
type LifecycleStep = {
    id: string;
    runningTexts: string[];
    doneText: string;
    getState: (env: PlainEnvironment) => StepState;
};

const LIFECYCLE_STEPS: LifecycleStep[] = [
    {
        id: "machine",
        runningTexts: [
            "I'm setting up your environment now. This usually takes under a minute.",
        ],
        doneText: "✅ Your environment machine is ready.",
        getState: stepStartState,
    },
    {
        id: "clone",
        runningTexts: [
            "I'm cloning your repository and getting dependencies installed.",
            "While that runs, I'm scanning your project to understand the languages, frameworks, and build tools you're using.",
            "I'm reading through your configuration files to get a sense of how everything is wired together.",
            "I looked for an AGENTS.md in your repo root — if you add one, I'll use it to pick up project-specific context automatically.",
            "I'm indexing your source files now so I can quickly navigate and search across the codebase when you need me to.",
            "I'm mapping out the module boundaries and key entry points so I know where things live.",
        ],
        doneText: "✅ Repository cloned and project indexed.",
        getState: stepCloneState,
    },
    {
        id: "integrations",
        runningTexts: [
            "I'm checking what integrations are available for your organization.",
            "Integrations are enabled ✅ If you connect your personal accounts in Settings → Integrations, I'll be able to work with Linear, Slack, GitHub, and more on your behalf.",
        ],
        doneText: "✅ Integrations are enabled and ready to use.",
        getState: stepAutomationsState,
    },
    {
        id: "devcontainer",
        runningTexts: [
            "I'm building the dev container for your project. The first build takes a bit longer, but future starts will be much faster.",
        ],
        doneText: "✅ Dev container is running. Your tools and dependencies are ready.",
        getState: (env) => stepDevcontainerState(env).state,
    },
];

const WELCOME_MESSAGES: Record<number, string[]> = {
    2: [
        "Welcome back — I'm spinning up your environment now.",
        "By the way, if you create a Project for this repo, I can use prebuilds to get your environment ready faster next time.",
        "With a Project, I'll automatically rebuild your dev container whenever your repo changes — so you won't have to wait.",
    ],
    3: [
        "Starting up your environment — this should be quick.",
        "Here's something that might save you time: you can set up Automations so I run tasks like linting or tests automatically whenever your environment starts.",
        "Also, you can work with me from VS Code Desktop or JetBrains if you prefer — you can open your repo in VS Code by clicking on the \"Open VS Code\" button found in the top right hand corner.",
    ],
};

/**
 * Derives welcome message fragments from the environment's lifecycle state.
 * Each lifecycle step maps to a fragment that appears when the step starts running
 * and marks as completed when the step succeeds. Steps that are Empty are skipped.
 *
 * On page refresh, the environment state is already populated so fragments
 * sync immediately — no timers or localStorage needed.
 */
type WelcomeFragment = { type: "text"; id: string; content: string; completed: boolean };

/**
 * Messages shown after all lifecycle steps complete but before the agent's first reply.
 * Fills the gap while the agent is reading the codebase and preparing its response.
 */
const READY_TEXTS: string[] = [
    "Your environment is ready. I'm reading through the codebase now to understand your project.",
    "I'm reviewing the project structure, dependencies, and build configuration.",
    "Almost there — just putting together my initial assessment.",
];

/**
 * Computes the current set of welcome fragments from the environment lifecycle state.
 * Returns only the fragments that should be visible right now (up to the currently running step).
 * When all lifecycle steps are done and the agent hasn't replied yet, appends "ready" fragments.
 */
function computeFragments(environment: PlainEnvironment, hasAgentReply: boolean): WelcomeFragment[] {
    const fragments: WelcomeFragment[] = [];
    let allDone = true;

    for (const step of LIFECYCLE_STEPS) {
        const state = step.getState(environment);

        if (state === StepState.Empty) {
            continue;
        }

        if (state === StepState.Running) {
            allDone = false;
            step.runningTexts.forEach((text, i) => {
                fragments.push({
                    type: "text" as const,
                    id: `welcome-step-${step.id}-${i}`,
                    content: text,
                    completed: i < step.runningTexts.length - 1,
                });
            });
            break;
        }

        if (state === StepState.Success || state === StepState.Warning) {
            step.runningTexts.forEach((text, i) => {
                fragments.push({
                    type: "text" as const,
                    id: `welcome-step-${step.id}-${i}`,
                    content: text,
                    completed: true,
                });
            });
            fragments.push({
                type: "text" as const,
                id: `welcome-step-${step.id}-done`,
                content: step.doneText,
                completed: true,
            });
            continue;
        }

        if (state === StepState.Failure) {
            allDone = false;
            step.runningTexts.forEach((text, i) => {
                fragments.push({
                    type: "text" as const,
                    id: `welcome-step-${step.id}-${i}`,
                    content: text,
                    completed: true,
                });
            });
            break;
        }

        // Waiting — not done yet
        allDone = false;
    }

    // After all lifecycle steps complete, show "ready" texts until the agent replies
    if (allDone && fragments.length > 0) {
        READY_TEXTS.forEach((text, i) => {
            fragments.push({
                type: "text" as const,
                id: `welcome-step-ready-${i}`,
                content: text,
                completed: hasAgentReply || i < READY_TEXTS.length - 1,
            });
        });
    }

    return fragments;
}

const FIRST_RUN_FRAGMENTS_KEY = "FIRST_RUN_FRAGMENTS";

type PersistedFirstRun = {
    fragments: WelcomeFragment[];
    /** Message ID after which these fragments were inserted */
    afterMessageId: string | number | null;
};

function getPersistedFirstRun(environmentId: string): PersistedFirstRun | null {
    try {
        const raw = localStorage.getItem(`${FIRST_RUN_FRAGMENTS_KEY}_${environmentId}`);
        return raw ? (JSON.parse(raw) as PersistedFirstRun) : null;
    } catch {
        return null;
    }
}

function persistFirstRun(environmentId: string, data: PersistedFirstRun) {
    try {
        localStorage.setItem(`${FIRST_RUN_FRAGMENTS_KEY}_${environmentId}`, JSON.stringify(data));
    } catch {
        // localStorage may be unavailable
    }
}

/**
 * Manages first-run welcome messages with persistence.
 *
 * - For startCount 1-3: generates lifecycle messages from env state, accumulates
 *   them so they never shrink, and persists them once the agent replies.
 * - For startCount > 3 (or on reload after persistence): returns the persisted
 *   fragments as completed messages at their anchored position.
 * - If no persisted data exists and startCount > 3: returns nothing.
 */
type UseWelcomeMessagesOptions = {
    environment: PlainEnvironment | undefined;
    startCount: number | undefined;
    hasAgentReply: boolean;
    environmentId: string | undefined;
    messages: Message[];
    isWelcomeEligible: boolean;
    markWelcomeShown: () => Promise<void>;
};

function useWelcomeMessages({ environment, startCount, hasAgentReply, environmentId, messages, isWelcomeEligible, markWelcomeShown }: UseWelcomeMessagesOptions): { firstRunMessages: Message[]; firstRunAnchorId: string | number | null } {
    const accumulatedRef = useRef<Map<string, WelcomeFragment>>(new Map());
    const persistedOnceRef = useRef(false);

    // Check for persisted first-run fragments
    const persisted = useMemo(
        () => (environmentId ? getPersistedFirstRun(environmentId) : null),
        [environmentId],
    );

    const isActive = !persisted && isWelcomeEligible && startCount !== undefined && startCount >= 1 && startCount <= 3 && !!environment;

    const firstRunMessages: Message[] = useMemo(() => {
        // Already persisted — return stored fragments
        if (persisted) {
            return [{
                id: -1,
                type: "agent" as const,
                fragments: persisted.fragments.map((f) => ({ ...f, completed: true })),
            }];
        }

        if (!isActive || !environment) {
            return [];
        }

        const current = computeFragments(environment, hasAgentReply);
        const accumulated = accumulatedRef.current;

        for (const frag of current) {
            const existing = accumulated.get(frag.id);
            if (!existing) {
                accumulated.set(frag.id, { ...frag });
            } else if (frag.completed && !existing.completed) {
                existing.completed = true;
            }
        }

        if (accumulated.size === 0) {
            return [];
        }

        const fragments = Array.from(accumulated.values());

        if (hasAgentReply) {
            for (const frag of fragments) {
                frag.completed = true;
            }
        }

        return [{ id: -1, type: "agent" as const, fragments }];
    }, [environment, persisted, isActive, hasAgentReply]);

    // Persist once the agent replies and we have accumulated fragments
    useEffect(() => {
        if (
            !persistedOnceRef.current &&
            isActive &&
            hasAgentReply &&
            environmentId &&
            accumulatedRef.current.size > 0
        ) {
            persistedOnceRef.current = true;
            const lastUserMsg = messages.findLast((m) => m.type === "user");
            persistFirstRun(environmentId, {
                fragments: Array.from(accumulatedRef.current.values()).map((f) => ({
                    ...f,
                    completed: true,
                })),
                afterMessageId: lastUserMsg?.id ?? null,
            });

            // After the last eligible run, mark welcome messages as fully shown
            if (startCount !== undefined && startCount >= 3) {
                void markWelcomeShown();
            }
        }
    }, [isActive, hasAgentReply, environmentId, messages, startCount, markWelcomeShown]);

    const firstRunAnchorId = persisted ? persisted.afterMessageId : null;

    return { firstRunMessages, firstRunAnchorId };
}

type WelcomeBackAnchor = {
    count: number;
    /** ID of the last message in the stream when this welcome-back was triggered */
    afterMessageId: string | number | null;
};

function getStoredAnchors(environmentId: string): WelcomeBackAnchor[] {
    try {
        const raw = localStorage.getItem(`${WELCOME_BACK_ANCHORS_KEY}_${environmentId}`);
        return raw ? (JSON.parse(raw) as WelcomeBackAnchor[]) : [];
    } catch {
        return [];
    }
}

function storeAnchors(environmentId: string, anchors: WelcomeBackAnchor[]) {
    try {
        localStorage.setItem(`${WELCOME_BACK_ANCHORS_KEY}_${environmentId}`, JSON.stringify(anchors));
    } catch {
        // localStorage may be unavailable
    }
}

/**
 * Tracks welcome-back message positions across restarts.
 * When startCount increases, snapshots the last message ID in the stream
 * and persists it to localStorage so the position survives page refresh.
 */
function useWelcomeBackAnchors(
    messages: Message[],
    startCount: number | undefined,
    environmentId: string | undefined,
    isWelcomeEligible: boolean,
): WelcomeBackAnchor[] {
    const [anchors, setAnchors] = useState<WelcomeBackAnchor[]>(() =>
        environmentId ? getStoredAnchors(environmentId) : [],
    );
    const prevCountRef = useRef<number | undefined>(undefined);

    useEffect(() => {
        if (!isWelcomeEligible || !environmentId || startCount === undefined || startCount < 2) {
            prevCountRef.current = startCount;
            return;
        }

        const prevCount = prevCountRef.current;
        prevCountRef.current = startCount;

        if (prevCount === startCount) {
            return;
        }

        // startCount changed — capture anchors for any new counts
        const existing = getStoredAnchors(environmentId);
        const existingCounts = new Set(existing.map((a) => a.count));
        const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
        const lastMessageId = lastMessage?.id ?? null;

        const newAnchors: WelcomeBackAnchor[] = [];
        for (let count = 2; count <= startCount; count++) {
            if (!existingCounts.has(count) && WELCOME_MESSAGES[count]) {
                newAnchors.push({ count, afterMessageId: lastMessageId });
            }
        }

        if (newAnchors.length > 0) {
            const updated = [...existing, ...newAnchors];
            storeAnchors(environmentId, updated);
            setAnchors(updated);
        } else if (existing.length !== anchors.length) {
            setAnchors(existing);
        }
    }, [isWelcomeEligible, startCount, environmentId, messages, anchors.length]);

    return anchors;
}

/** Delay between revealing each welcome-back fragment */
const WELCOME_BACK_REVEAL_DELAY = 5000;

/**
 * Progressively reveals welcome-back fragments one at a time.
 * Returns the number of fragments to show for each anchor count.
 */
function useWelcomeBackReveal(anchors: WelcomeBackAnchor[]): Map<number, number> {
    const [revealedCounts, setRevealedCounts] = useState<Map<number, number>>(() => {
        const map = new Map<number, number>();
        for (const anchor of anchors) {
            const texts = WELCOME_MESSAGES[anchor.count];
            if (texts) {
                // If anchor was persisted from a previous session, show all
                map.set(anchor.count, texts.length);
            }
        }
        return map;
    });
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        // Find the first anchor that hasn't fully revealed yet
        let needsReveal: { count: number; total: number; revealed: number } | null = null;
        for (const anchor of anchors) {
            const texts = WELCOME_MESSAGES[anchor.count];
            if (!texts) continue;
            const revealed = revealedCounts.get(anchor.count) ?? 1;
            if (revealed < texts.length) {
                needsReveal = { count: anchor.count, total: texts.length, revealed };
                break;
            }
        }

        if (!needsReveal) return;

        const { count } = needsReveal;
        timerRef.current = setTimeout(() => {
            setRevealedCounts((prev) => {
                const next = new Map(prev);
                next.set(count, (next.get(count) ?? 1) + 1);
                return next;
            });
        }, WELCOME_BACK_REVEAL_DELAY);

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [anchors, revealedCounts]);

    // Ensure new anchors start at 1
    useEffect(() => {
        setRevealedCounts((prev) => {
            let changed = false;
            const next = new Map(prev);
            for (const anchor of anchors) {
                if (!next.has(anchor.count) && WELCOME_MESSAGES[anchor.count]) {
                    next.set(anchor.count, 1);
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
    }, [anchors]);

    return revealedCounts;
}
